import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput, baseToolName } from "./shared/utils";

interface DocActionResult {
  ok: true;
  // docs_create
  documentId?: string;
  title?: string;
  documentTitle?: string;
  webViewLink?: string;
  url?: string;
  // docs_append / docs_replace
  replacements?: number;
  message?: string;
}

interface DocActionCardProps {
  part: ToolUIPart;
}

export const DocActionCard = memo(function DocActionCard({ part }: DocActionCardProps) {
  const isStreaming = part.state === "input-streaming";
  const tool = baseToolName(part.toolName);

  const result = useMemo(
    () => parseToolOutput<DocActionResult>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const data = result.kind === "ok" ? result.data : null;

  function getHeadline(): string {
    if (isStreaming) return "Working...";
    if (isError) return "Docs Error";
    if (!data) return "Done";
    switch (tool) {
      case "docs_create": {
        const docTitle = data.title ?? data.documentTitle;
        return docTitle ? `Created "${docTitle}"` : "Document created";
      }
      case "docs_append":
        return "Content appended";
      case "docs_replace": {
        const n = data.replacements ?? 0;
        return n > 0 ? `Replaced ${n} instance${n !== 1 ? "s" : ""}` : "Replacements done";
      }
      default:
        return "Document updated";
    }
  }

  function getDetail(): string | null {
    if (!data) return null;
    return data.message ?? null;
  }

  const link = data?.webViewLink ?? data?.url;
  const headline = getHeadline();
  const detail = getDetail();
  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const accent = isError ? "error" : "docs";

  return (
    <CardShell
      icon={isError ? "✗" : "◻"}
      title={headline}
      accent={accent}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">⋯</span>
          <div className="tool-card__status-text">Updating document...</div>
        </div>
      )}
      {isError && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✗</span>
          <div>
            <div className="tool-card__status-text">Docs operation failed</div>
            <div className="tool-card__status-detail">{result.kind === "error" ? result.error : "Error"}</div>
          </div>
        </div>
      )}
      {!isStreaming && !isError && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✓</span>
          <div>
            {detail && (
              <div className="tool-card__status-detail">{detail}</div>
            )}
            {link && (
              <a
                className="tool-card__doc-link-inline"
                href={link}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={`Open ${data?.title ?? data?.documentTitle ?? "document"} in Docs (opens in new tab)`}
              >
                Open document ↗
              </a>
            )}
          </div>
        </div>
      )}
    </CardShell>
  );
});
